import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/lib/apiClient';
import { openOAuthPopup } from './OAuthPopup';
import { toast } from 'sonner';

export function LoginModal() {
  const { loginModalOpen, setLoginModalOpen, setAuth } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await apiClient.post('/api/v1/auth/login', { email, password });
      localStorage.removeItem('trainer-sidebar-collapsed');
      setAuth(data.access_token);
      setLoginModalOpen(false);
    } catch {
      toast.error('Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'github') => {
    try {
      const token = await openOAuthPopup(provider);
      localStorage.removeItem('trainer-sidebar-collapsed');
      setAuth(token);
      setLoginModalOpen(false);
    } catch (err) {
      if (err instanceof Error && err.message !== 'Login cancelled') {
        toast.error(err.message);
      }
    }
  };

  return (
    <Dialog.Root open={loginModalOpen} onOpenChange={setLoginModalOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-8 shadow-2xl">
          <Dialog.Title className="text-2xl font-bold font-heading">Sign In</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            Welcome to KodPrep. Sign in to continue.
          </Dialog.Description>

          {/* OAuth buttons */}
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={() => handleOAuth('google')}
              className="flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"
            >
              Continue with Google
            </button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">OR</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Email/password form */}
          <form onSubmit={handleEmailLogin} className="flex flex-col gap-4">
            <input
              type="email" placeholder="Email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              type="password" placeholder="Password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="submit" disabled={isSubmitting}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            No account? Contact your organization admin for an invite.
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
